import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui'
import { Button } from '@/components/ui'
import { PureLoginForm } from '../auth/pure-login-form'
import { PaymentForm, PaymentFormSkeleton } from './payment-form'
import { StripeError } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { toast } from '@/components/ui'
import useSWR, { mutate } from 'swr'
import { createClient } from '@/lib/supabase/client'
import { useSubscription } from '../../hooks/use-subscription'
import { useUser } from '../../hooks/use-user'
import { trackAction } from '../../lib/tracking'
import { Zap, Lock, BarChart2, Database, FileText, MessageSquare } from 'lucide-react'
import { TrustBuilderLogos } from '../trust-builder'

const STRIPE_PUBLISHABLE_KEY = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
if (!STRIPE_PUBLISHABLE_KEY) {
  throw new Error('Missing NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY')
}

// Initialize Stripe outside of component to prevent multiple instances
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY)

interface AlreadySubscribedDialogProps {
  open: boolean
  onClose: () => void
}

function AlreadySubscribedDialog({ open, onClose }: AlreadySubscribedDialogProps) {
  const [billingUrl, setBillingUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    const fetchBillingUrl = async () => {
      try {
        setIsLoading(true)
        const response = await fetch('/api/integrations/stripe/billing', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        
        const data = await response.json()
        
        if (!response.ok) {
          throw new Error(data.message || 'Failed to get billing portal URL')
        }
        
        if (data.url) {
          setBillingUrl(data.url)
        } else {
          throw new Error('No billing portal URL returned')
        }
      } catch (error: unknown) {
        console.error('Error accessing billing portal:', error)
        toast.error(error instanceof Error ? error.message : 'Failed to access billing portal')
      } finally {
        setIsLoading(false)
      }
    }

    if (open) {
      fetchBillingUrl()
    }
  }, [open])
  
  const handleBillingClick = () => {
    if (billingUrl) {
      window.location.href = billingUrl
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Features Section */}
          <div className="space-y-6 p-6 bg-background/5 rounded-lg">
            <h3 className="text-xl font-semibold text-supernova-pink">Pro Features</h3>
            <div className="space-y-4">
              <FeatureItem 
                icon={<MessageSquare size={20} />} 
                text="1000+ AI messages per month"
              />
              <FeatureItem 
                icon={<BarChart2 size={20} />} 
                text="Unlimited data visualization"
              />
              <FeatureItem 
                icon={<Database size={20} />} 
                text="Unlimited statistical analysis"
              />
              <FeatureItem 
                icon={<FileText size={20} />} 
                text="Unlimited SQL queries"
              />
              <FeatureItem 
                icon={<Zap size={20} />} 
                text="Unlimited format conversion"
              />
              <FeatureItem 
                icon={<Lock size={20} />} 
                text="Priority support"
              />
            </div>
          </div>

          {/* Already Subscribed Section */}
          <div>
            <DialogHeader>
              <DialogTitle>Already Subscribed</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p>You already have an active subscription.</p>
              <Button 
                className="w-full bg-supernova-pink hover:bg-supernova-pink/90" 
                onClick={handleBillingClick}
                disabled={isLoading || !billingUrl}
              >
                {isLoading ? 'Loading...' : 'Manage Billing'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface UpgradeModalProps {
  open: boolean
  onClose: () => void
  initialMode?: 'login' | 'signup'
  defaultBillingInterval?: 'monthly' | 'annual'
  openTrigger?: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'signin_button' | 'create_dataset'
}

interface FeatureItemProps {
  icon: React.ReactNode
  text: string
}

function FeatureItem({ icon, text }: FeatureItemProps) {
  return (
    <div className="flex items-center gap-4">
      <div className="text-supernova-cyan text-2xl">{icon}</div>
      <span className="text-base text-supernova-200">{text}</span>
    </div>
  )
}

export function UpgradeModal({ 
  open, 
  onClose, 
  initialMode = 'signup', 
  defaultBillingInterval = 'monthly',
  openTrigger
}: UpgradeModalProps) {
  const [authMode, setAuthMode] = useState<'login' | 'signup'>(initialMode)
  const [billingInterval, setBillingInterval] = useState(defaultBillingInterval)
  const { hasSubscription, getClientSecrets } = useSubscription()
  const supabase = createClient()
  const { user } = useUser()
  
  // Update billing interval when defaultBillingInterval changes
  useEffect(() => {
    setBillingInterval(defaultBillingInterval)
  }, [defaultBillingInterval])

  // Get client secrets for payment
  const { data: clientSecrets } = useSWR(
    user && !hasSubscription ? 'setup-intent' : null,
    getClientSecrets
  )

  // Track initial view
  useEffect(() => {
    if (open) {
      trackAction('upgrade_modal_view', {
        view: user ? 'payment' : 'auth',
        ...(openTrigger && { openTrigger })
      })
    }
  }, [open, user, openTrigger])

  const handleEmailLogin = async (email: string) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/#pricing`,
        },
      })

      if (error) throw error
      toast.success('Check your email for the login link')
      trackAction('magic_link_sent', { email })
    } catch (err) {
      console.error('Error sending magic link:', err)
      toast.error('Failed to send login link')
      trackAction('upgrade_error', {
        error_message: err instanceof Error ? err.message : 'Unknown error',
        error_type: 'auth_error'
      })
    }
  }

  // Show login/signup form if not logged in
  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Features Section */}
            <div className="space-y-6 p-8 bg-supernova-900/50 rounded-xl">
              <h3 className="text-2xl font-semibold text-supernova-cyan">Pro Features</h3>
              <div className="space-y-6">
                <FeatureItem 
                  icon={<MessageSquare size={24} />} 
                  text="1000+ AI messages per month"
                />
                <FeatureItem 
                  icon={<BarChart2 size={24} />} 
                  text="Unlimited data visualization"
                />
                <FeatureItem 
                  icon={<Database size={24} />} 
                  text="Unlimited statistical analysis"
                />
                <FeatureItem 
                  icon={<FileText size={24} />} 
                  text="Unlimited SQL queries"
                />
                <FeatureItem 
                  icon={<Zap size={24} />} 
                  text="Unlimited format conversion"
                />
                <FeatureItem 
                  icon={<Lock size={24} />} 
                  text="Priority support"
                />
              </div>
            </div>

            {/* Auth Section */}
            <div>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-supernova-200">Step 1 of 2</span>
              </div>
              <div className="flex flex-col gap-4">
                <PureLoginForm
                  onEmailLogin={handleEmailLogin}
                  onGoogleLogin={async (credential) => {
                    try {
                      const { data, error } = await supabase.auth.signInWithIdToken({
                        provider: 'google',
                        token: credential,
                        nonce: ''
                      })
                      if (error) throw error
                      if (!data.session) throw new Error('No session')
                      
                      // Force revalidate the user from useUser hook
                      // The useUser hook will automatically update when auth state changes
                      
                      toast.success('Successfully signed in!')
                    } catch (error) {
                      console.error('Error signing in with Google:', error)
                      toast.error('Failed to sign in with Google')
                      trackAction('upgrade_modal_google_signin_error', { error: String(error) })
                    }
                  }}
                  onMicrosoftLogin={async (token) => {
                    try {
                      const { data, error } = await supabase.auth.signInWithOAuth({
                        provider: 'azure',
                        options: {
                          skipBrowserRedirect: true,
                          redirectTo: `${window.location.origin}/auth/callback`
                        }
                      })
                      if (error) throw error
                      toast.success('Successfully signed in!')
                    } catch (error) {
                      console.error('Error signing in with Microsoft:', error)
                      toast.error('Failed to sign in with Microsoft')
                      trackAction('upgrade_modal_microsoft_signin_error', { error: String(error) })
                    }
                  }}
                  onError={(error) => {
                    console.error('Auth error:', error)
                    toast.error('Authentication failed')
                    trackAction('upgrade_modal_auth_error', { error: String(error) })
                  }}
                  mode="signup"
                />
              </div>
            </div>
          </div>

          {/* Trust Builder Section */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <TrustBuilderLogos />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Show already subscribed dialog if user has subscription
  if (hasSubscription) {
    return <AlreadySubscribedDialog open={open} onClose={onClose} />
  }

  // Show payment form if we have client secrets
  if (!clientSecrets) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Features Section */}
            <div className="space-y-6 p-8 bg-supernova-900/50 rounded-xl">
              <h3 className="text-2xl font-semibold text-supernova-cyan">Pro Features</h3>
              <div className="space-y-6">
                <FeatureItem 
                  icon={<MessageSquare size={24} />} 
                  text="1000+ AI messages per month"
                />
                <FeatureItem 
                  icon={<BarChart2 size={24} />} 
                  text="Unlimited data visualization"
                />
                <FeatureItem 
                  icon={<Database size={24} />} 
                  text="Unlimited statistical analysis"
                />
                <FeatureItem 
                  icon={<FileText size={24} />} 
                  text="Unlimited SQL queries"
                />
                <FeatureItem 
                  icon={<Zap size={24} />} 
                  text="Unlimited format conversion"
                />
                <FeatureItem 
                  icon={<Lock size={24} />} 
                  text="Priority support"
                />
              </div>
            </div>

            {/* Loading Section */}
            <div>
              <DialogHeader className="pb-4">
                <DialogTitle className="text-2xl">Loading payment information...</DialogTitle>
              </DialogHeader>
              <PaymentFormSkeleton />
            </div>
          </div>

          {/* Trust Builder Section */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <TrustBuilderLogos />
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Features Section */}
          <div className="space-y-6 p-8 bg-supernova-900/50 rounded-xl">
            <h3 className="text-2xl font-semibold text-supernova-cyan">Pro Features</h3>
            <div className="space-y-6">
              <FeatureItem 
                icon={<MessageSquare size={24} />} 
                text="1000+ AI messages per month"
              />
              <FeatureItem 
                icon={<BarChart2 size={24} />} 
                text="Unlimited data visualization"
              />
              <FeatureItem 
                icon={<Database size={24} />} 
                text="Unlimited statistical analysis"
              />
              <FeatureItem 
                icon={<FileText size={24} />} 
                text="Unlimited SQL queries"
              />
              <FeatureItem 
                icon={<Zap size={24} />} 
                text="Unlimited format conversion"
              />
              <FeatureItem 
                icon={<Lock size={24} />} 
                text="Priority support"
              />
            </div>
          </div>

          {/* Payment Section */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-sm text-supernova-200">Step 2 of 2</span>
            </div>
            <DialogHeader className="pb-4">
              <DialogTitle className="text-2xl">Upgrade your account</DialogTitle>
            </DialogHeader>
            <Elements 
              stripe={stripePromise} 
              options={{
                clientSecret: clientSecrets[billingInterval],
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#0066FF'
                  }
                }
              }}
            >
              <PaymentForm
                billingInterval={billingInterval}
                onBillingIntervalChange={(newInterval) => {
                  setBillingInterval(newInterval)
                  trackAction('billing_interval_changed', {
                    from: billingInterval,
                    to: newInterval
                  })
                }}
                onSuccess={() => {
                  onClose()
                  trackAction('subscription_created', {
                    billing_interval: billingInterval
                  })
                }}
                onError={(error) => {
                  toast.error(error.message)
                  trackAction('upgrade_error', {
                    error_message: error.message || 'Unknown error',
                    error_type: 'stripe' in error ? 'stripe_error' : 'general_error'
                  })
                }}
                clientSecrets={clientSecrets}
              />
            </Elements>
          </div>
        </div>

        {/* Trust Builder Section */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <TrustBuilderLogos />
        </div>
      </DialogContent>
    </Dialog>
  )
} 